import React from 'react';
import { useNavigate } from 'react-router';
import { TableContainer, Table, TableRow, TableCell, TableBody, Card, Stack, Paper, Typography, Box } from '@mui/material';
import { Email, QrCode2, Groups } from '@mui/icons-material';
import Grid from '@mui/material/Grid';
import ReportsCardHeader from '../cards/ReportsCardHeader';
import ReportsCardContent from '../cards/ReportsCardContent';
import TypedValue from '../../TypedValue';
import { lighten } from '@mui/system';
import { useTheme } from '@mui/material/styles';
import { DataGrid } from '@mui/x-data-grid';
import { channelColours } from '../../theme/constants';
import { grey } from '@mui/material/colors';

const CardTable = ({ title, icon, data, total, actionType, columns, onClickRow }) => {
    const theme = useTheme();

    return (
        <Card sx={{ height: '100%' }}>
            <ReportsCardHeader
                title={`${title} (${data.length})`}
                icon={icon}
            />
            <ReportsCardContent>
                <Stack spacing={2}>
                    <Paper variant="outlined" sx={{ backgroundColor: lighten(theme.palette.primary.main, 0.9) }}>
                        <TableContainer>
                            <Table size="small">
                                <TableBody>
                                    <TableRow>
                                        <TableCell variant="head">
                                            Total {actionType}
                                        </TableCell>
                                        <TableCell align="right">
                                            <Typography variant="h6" color="primary">
                                                <TypedValue value={total} accuracy={0} type="number" />
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>
                    <Box sx={{ height: '40vh' }}>
                        <DataGrid
                            rows={data}
                            columns={columns}
                            initialState={{
                                pagination: {
                                    paginationModel: {
                                        pageSize: 10
                                    }
                                }
                            }}
                            pageSizeOptions={[10, 20, 50]}
                            disableRowSelectionOnClick
                            onRowClick={(params) => onClickRow(params.id)}
                            sx={{
                                '.MuiDataGrid-cell:focus': {
                                    outline: 'none'
                                },
                                '& .MuiDataGrid-row:hover': {
                                    cursor: 'pointer'
                                }
                            }}
                        />
                    </Box>
                </Stack>
            </ReportsCardContent>
        </Card>
    );
};

const SurveyChannels = ({ channels }) => {
    const navigate = useNavigate();

    const emailsTotal = channels.emails.map(e => e.surveyOpens).reduce((acc, cur) => acc + cur, 0);
    const qrTotal = channels.qrCodes.map(e => e.surveyOpens).reduce((acc, cur) => acc + cur, 0);
    const teamsTotal = channels.teamsCampaigns.map(e => e.surveyOpens).reduce((acc, cur) => acc + cur, 0);

    const emailColumns = [
        {
            field: 'name',
            headerName: 'Name',
            flex: 0.6
        },
        {
            field: 'subject',
            headerName: 'Subject',
            flex: 0.3
        },
        {
            field: 'surveyOpens',
            headerName: 'Opens',
            align: 'right',
            headerAlign: 'right'
        }
    ];

    const qrColumns = [
        {
            field: 'name',
            headerName: 'Name',
            flex: 0.6
        },
        {
            field: 'location',
            headerName: 'Location',
            flex: 0.3
        },
        {
            field: 'surveyOpens',
            headerName: 'Opens',
            align: 'right',
            headerAlign: 'right'
        }
    ];

    const teamsColumns = [
        {
            field: 'title',
            headerName: 'Title',
            flex: 0.6
        },
        {
            field: 'subtitle',
            headerName: 'Subtitle',
            flex: 0.3
        },
        {
            field: 'surveyOpens',
            headerName: 'Opens',
            align: 'right',
            headerAlign: 'right'
        }
    ];

    return (
        <Grid container spacing={2}>
            <Grid size={{ xs: 12, lg: 4 }}>
                <CardTable
                    title="Emails"
                    icon={<Email color="primary" />}
                    data={channels.emails}
                    total={emailsTotal}
                    actionType="Clicks"
                    columns={emailColumns}
                    onClickRow={id => navigate(`/newreports/emails/${id}`)}
                />
            </Grid>
            <Grid size={{ xs: 12, lg: 4 }}>
                <CardTable
                    title="QR Codes"
                    icon={<QrCode2 sx={{ color: grey[600] }} />}
                    data={channels.qrCodes}
                    total={qrTotal}
                    actionType="Opens"
                    columns={qrColumns}
                    onClickRow={id => navigate(`/editor/qr/${id}`)}
                />
            </Grid>
            <Grid size={{ xs: 12, lg: 4 }}>
                <CardTable
                    title="Teams Campaigns"
                    icon={<Groups sx={{ color: channelColours.teams }} />}
                    data={channels.teamsCampaigns}
                    total={teamsTotal}
                    actionType="Clicks"
                    columns={teamsColumns}
                    onClickRow={() => navigate('/editor', { state: { type: 'teams' } })}
                />
            </Grid>
        </Grid>
    );
};

export default SurveyChannels;